import { ColumnDetails } from '../../../../grid/types/common';
import { gridCustomHtmlRenderer } from '../../../../grid/utils/renderers';
import { ColumnIndexes, ColumnKeys } from '../types/licensing-grid-columns';
import { LicenseRenderers } from '../../licenseRenderers/LicenseRenderers';
import { LicenseUsage } from '../types/licensing-grid-license';
import { Bundle } from '../../../../license/types/Bundles';
import { html, TemplateResult } from 'lit';
import { Feature } from '@nirvana/shared/constants';
import { Target } from '../../../../ui/gzLinkComponent/types';
import { buildHash } from '@nirvana/shared/navigation';
import { SwitchValue } from '../../../myCompany/purchase/purchasePage/types';

export function renderProductStatus(rowData, columnDetails: ColumnDetails<ColumnIndexes, ColumnKeys>) {
  const productStatus = rowData.item[columnDetails.key];
  return LicenseRenderers.renderProductStatus(productStatus);
}

export function renderProductType(rowData, columnDetails: ColumnDetails<ColumnIndexes, ColumnKeys>) {
  const productTypeName = LicenseRenderers.getTranslatedProductType(
    rowData.item.isMonthly,
    rowData.item.isAddon,
    rowData.item[columnDetails.key]
  );

  return gridCustomHtmlRenderer(`<span class='cell-label'>${productTypeName}</span>`);
}

export function renderProtectionModel(rowData: unknown & { item: LicenseUsage }): DocumentFragment {
  const protectionModel = LicenseRenderers.renderProtectionModel(rowData.item.protectionModel ?? Bundle.UNDEFINED);
  return gridCustomHtmlRenderer(`<span class='cell-label'>${protectionModel}</span>`);
}

export function renderProductName(rowData, columnDetails: ColumnDetails<ColumnIndexes, ColumnKeys>) {
  const productName = LicenseRenderers.getTranslatedProductName(
    rowData.item.isMonthly,
    rowData.item.isInheriting,
    rowData.item.hasBasicMonthly,
    rowData.item[columnDetails.key]
  );

  return gridCustomHtmlRenderer(`<span class='cell-label'>${productName}</span>`);
}

export function renderExpiryDate(rowData: { item: LicenseUsage }) {
  const productId = rowData.item.productId;
  const expiryDate = LicenseRenderers.renderExpiryDate(rowData.item.expiryDate, this.dependencies.getTimezone());
  const shouldRenderInlineButton =
    Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_IN_PRODUCT_BUY]) && !rowData.item.isInProductTrial;

  return html`
    <div class="container" part="item-with-btn">
      <gz-tooltip tooltipText="${expiryDate}" isEllipsis> ${expiryDate} </gz-tooltip>
      <!-- show extend button only if inProductBuy feature is available -->
      ${shouldRenderInlineButton ? getExtendExpiryDateInlineButton(productId) : ''}
    </div>
  `;
}

export function renderSubscriptionEndDate(rowData: unknown & { item: LicenseUsage }) {
  return LicenseRenderers.renderSubscriptionEndDate(rowData.item, this.dependencies.getTimezone());
}

export function getTotalSeatsRenderer(rowData: { item: LicenseUsage }) {
  const productId = rowData.item.productId;
  const totalSeats = LicenseRenderers.renderTotalSeats(rowData.item);
  const shouldRenderInlineButton =
    Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_IN_PRODUCT_BUY]) && !rowData.item.isInProductTrial;

  return html`
    <div class="container" part="item-with-btn-seats">
      <gz-tooltip tooltipText="${totalSeats}" isEllipsis part="item-text">${totalSeats}</gz-tooltip>

      <!-- show extend button only if inProductBuy feature is available -->
      ${shouldRenderInlineButton
        ? html`
            <gz-link
              target="${Target.SELF}"
              href="${buildHash('/my-company/purchase', { productId, extend: SwitchValue.ExtendSeats })}"
              type="quiet"
            >
              <gz-icon name="ds-icon-add" size="20"></gz-icon>
            </gz-link>
          `
        : ''}
    </div>
  `;
}

export function getUsageBreakdownRenderer(rowData: unknown & { item: LicenseUsage }) {
  return gridCustomHtmlRenderer(`<span class='cell-label'>
    ${LicenseRenderers.renderUsageBreakdown(rowData.item)}
  </span>`);
}

export function getAutoRenewalRenderer(rowData: unknown & { item: LicenseUsage }) {
  return LicenseRenderers.renderAutoRenewal(rowData.item);
}

function getExtendExpiryDateInlineButton(productId: number): TemplateResult {
  return html`
    <gz-link
      target="${Target.SELF}"
      href="${buildHash('/my-company/purchase', { productId, extend: SwitchValue.ExtendPeriod })}"
      type="quiet"
    >
      <gz-icon size="24">
        <svg slot="custom-svg" viewBox="-2 -4 26 26">
          <path
            d="M17.45 0.833333H16.6167C16.1583 0.833333 15.7833 1.20833 15.7833 1.66667C15.7833 2.125 16.1583 2.5 16.6167 2.5H17.45C17.5167 2.5 17.5833 2.5 17.6417 2.51667C18.0917 2.61667 18.375 3.05833 18.2833 3.5L16.9417 9.73333C16.8583 10.125 16.5083 10.4 16.1083 10.3917H7.60833C7.20833 10.3917 6.85833 10.1167 6.775 9.725L4.98333 0.666667C4.90833 0.275 4.55833 0 4.15833 0H0.833333C0.375 0 0 0.375 0 0.833333C0 1.29167 0.375 1.66667 0.833333 1.66667H3.475L5.15 10.05C5.38333 11.2167 6.40833 12.05 7.59167 12.05H16.1083C17.2917 12.0667 18.3333 11.25 18.5833 10.0917L19.9417 3.85833C19.9833 3.66667 20 3.46667 20 3.26667C19.9667 1.89167 18.825 0.8 17.45 0.833333Z"
          />
          <path
            d="M8.30832 15C6.93332 15 5.81665 16.1167 5.81665 17.4917C5.81665 18.8667 6.93332 19.9833 8.30832 19.9833C9.68332 19.9833 10.8 18.8667 10.8 17.4917C10.8 16.1167 9.68332 15 8.30832 15ZM8.30832 18.325C7.84998 18.325 7.47498 17.95 7.47498 17.4917C7.47498 17.0333 7.84998 16.6583 8.30832 16.6583C8.76665 16.6583 9.14165 17.0333 9.14165 17.4917C9.14165 17.95 8.76665 18.325 8.30832 18.325Z"
          />
          <path
            d="M15.7917 15C14.4167 15 13.3 16.1167 13.3 17.4917C13.3 18.8667 14.4167 19.9833 15.7917 19.9833C17.1667 19.9833 18.2834 18.8667 18.2834 17.4917C18.2834 16.1167 17.1667 15 15.7917 15ZM15.7917 18.325C15.3334 18.325 14.9584 17.95 14.9584 17.4917C14.9584 17.0333 15.3334 16.6583 15.7917 16.6583C16.25 16.6583 16.625 17.0333 16.625 17.4917C16.625 17.95 16.25 18.325 15.7917 18.325Z"
          />
          <path
            d="M11.1332 8.82511H12.0832V5.96678H14.9416V5.01678H12.0832V2.15845H11.1332V5.01678H8.2749V5.96678H11.1332V8.82511Z"
          />
        </svg>
      </gz-icon>
    </gz-link>
  `;
}
